import { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { formatDateTime } from "@/lib/utils";
import { FolderCheck, ShieldCheck, ShieldOff } from "lucide-react";

// Espace freelance pro (05/10/2026) : état du dossier d'accueil de chaque
// freelance (identité, entreprise, RIB masqué, contrat), soldes de cagnotte,
// et marquage « documents vérifiés ». L'IBAN complet n'est JAMAIS affiché :
// le serveur ne le renvoie pas (seuls les 4 derniers caractères).
// Endpoints : admin.commissions.freelances / freelanceProfile / setDocumentsVerified.

const eur = (c: number | null | undefined) => (c == null ? "-" : (c / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" }));
const LEGAL: Record<string, string> = { MICRO: "Micro-entreprise", EI: "EI", EURL: "EURL", SASU: "SASU", SARL: "SARL", SAS: "SAS", AUTRE: "Autre" };

function Step({ ok, label }: { ok: boolean; label: string }) {
  return <Badge variant={ok ? "default" : "outline"} className="text-[11px]">{label}</Badge>;
}

export function FreelanceDossiersPanel() {
  const utils = trpc.useUtils();
  const q = trpc.admin.commissions.freelances.useQuery();
  const [open, setOpen] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const detail = trpc.admin.commissions.freelanceProfile.useQuery({ userId: open ?? "" }, { enabled: !!open });
  const verify = trpc.admin.commissions.setDocumentsVerified.useMutation({
    onSuccess: (_r, v) => {
      toast.success(v.verified ? "Documents marqués vérifiés." : "Vérification retirée.");
      setNote("");
      void utils.admin.commissions.freelances.invalidate();
      void utils.admin.commissions.freelanceProfile.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const rows = q.data ?? [];
  const d = detail.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><FolderCheck className="h-4 w-4" /> Dossiers et cagnottes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Freelance</TableHead>
                <TableHead>Dossier</TableHead>
                <TableHead>Entreprise</TableHead>
                <TableHead>Vérifié</TableHead>
                <TableHead className="text-right">Disponible</TableHead>
                <TableHead className="text-right">Demandé</TableHead>
                <TableHead className="text-right">Versé</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((f) => (
                <TableRow key={f.user.id}>
                  <TableCell>
                    <div className="font-medium">{[f.user.firstName, f.user.lastName].filter(Boolean).join(" ") || f.user.email}</div>
                    <div className="text-xs text-muted-foreground">{f.user.email} · {f.user.staffRole === "FREELANCE_SDR" ? "SDR" : "Sales"}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      <Step ok={f.steps.identity} label="Identité" />
                      <Step ok={f.steps.company} label="Entreprise" />
                      <Step ok={f.steps.bank} label="RIB" />
                      <Step ok={f.steps.contract} label="Contrat" />
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{f.profile?.companyName ?? <span className="text-muted-foreground">-</span>}{f.profile?.siret ? <div className="text-xs text-muted-foreground">SIRET {f.profile.siret}</div> : null}</TableCell>
                  <TableCell>{f.profile?.documentsVerifiedAt ? <Badge variant="default">Oui</Badge> : f.profile?.onboardingCompletedAt ? <Badge variant="warning">À vérifier</Badge> : <span className="text-xs text-muted-foreground">Incomplet</span>}</TableCell>
                  <TableCell className="text-right">{eur(f.balances.availableCents)}</TableCell>
                  <TableCell className="text-right">{eur(f.balances.requestedCents)}</TableCell>
                  <TableCell className="text-right">{eur(f.balances.paidCents)}</TableCell>
                  <TableCell><Button size="sm" variant="outline" onClick={() => setOpen(open === f.user.id ? null : f.user.id)}>{open === f.user.id ? "Fermer" : "Détail"}</Button></TableCell>
                </TableRow>
              ))}
              {q.data && rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-sm text-muted-foreground">Aucun freelance.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {open && d && (
          <div className="rounded-md border p-4 space-y-3 text-sm">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="font-medium">{[d.user.firstName, d.user.lastName].filter(Boolean).join(" ") || d.user.email} · {d.user.phone ?? "téléphone non renseigné"}</div>
              <div className="text-xs text-muted-foreground">Compte créé le {formatDateTime(d.user.createdAt)} · 2FA {d.user.totpEnabled ? "active" : "non activée"}</div>
            </div>
            {d.profile ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase">Entreprise</p>
                  <p>{d.profile.companyName ?? "-"} ({LEGAL[d.profile.legalStatus ?? ""] ?? d.profile.legalStatus ?? "-"})</p>
                  <p>SIRET {d.profile.siret ?? "-"}{d.profile.vatNumber ? ` · TVA ${d.profile.vatNumber}` : ""} · {d.profile.vatRegime === "ASSUJETTI" ? "Assujetti TVA" : "Franchise en base"}</p>
                  <p>{[d.profile.addressLine1, d.profile.addressLine2, [d.profile.postalCode, d.profile.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "-"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase">RIB et contrat</p>
                  <p>IBAN <span className="font-mono">{d.profile.ibanMasked ?? "-"}</span>{d.profile.ibanHolder ? ` · ${d.profile.ibanHolder}` : ""}{d.profile.bic ? ` · BIC ${d.profile.bic}` : ""}</p>
                  <p>Contrat {d.profile.contractVersion ?? "-"} accepté {d.profile.contractAcceptedAt ? `le ${formatDateTime(d.profile.contractAcceptedAt)} (IP ${d.profile.contractAcceptedIp ?? "?"})` : ": non"}</p>
                  <p>Mandat d'autofacturation {d.profile.selfBillingAcceptedAt ? `accepté le ${formatDateTime(d.profile.selfBillingAcceptedAt)}` : ": non"}</p>
                  <p>Dossier complet {d.profile.onboardingCompletedAt ? `le ${formatDateTime(d.profile.onboardingCompletedAt)}` : ": non"}</p>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">Ce freelance n'a pas encore commencé son dossier.</p>
            )}
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
              {d.profile?.documentsVerifiedAt ? (
                <>
                  <span className="text-xs text-muted-foreground">Vérifié le {formatDateTime(d.profile.documentsVerifiedAt)}{d.profile.verificationNote ? ` · ${d.profile.verificationNote}` : ""}</span>
                  <Button size="sm" variant="outline" disabled={verify.isLoading} onClick={() => verify.mutate({ userId: d.user.id, verified: false, note: note || null })}><ShieldOff className="h-3.5 w-3.5 mr-1" /> Retirer la vérification</Button>
                </>
              ) : (
                <>
                  <Input className="max-w-xs h-8" placeholder="Note (ex. Kbis et RIB contrôlés)" value={note} onChange={(e) => setNote(e.target.value)} />
                  <Button size="sm" disabled={!d.profile?.onboardingCompletedAt || verify.isLoading} onClick={() => verify.mutate({ userId: d.user.id, verified: true, note: note || null })}><ShieldCheck className="h-3.5 w-3.5 mr-1" /> Marquer documents vérifiés</Button>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
